/**
 * ================================================================
 * CUSTOM HOOK: useFirebaseNotifications
 * ================================================================
 *
 * @hook             useFirebaseNotifications
 * @description      Hook for managing Firebase Cloud Messaging notifications
 * @category         Business Logic Hook
 *
 * @parameters
 * - config?: FirebaseNotificationConfig - Configuration options
 *
 * @returns
 * - isInitialized: boolean - Firebase initialization status
 * - isLoading: boolean - Loading state for initialization/token ops
 * - hasPermission: boolean - Notification permission status
 * - token: string | null - Current FCM token
 * - error: string | null - Error state
 * - requestPermission: Function - Request notification permission
 * - generateToken: Function - Generate/refresh FCM token
 * - refreshToken: Function - Refresh existing token
 * - clearToken: Function - Clear stored token
 *
 * @sideEffects
 * - Firebase initialization: Sets up Firebase app and messaging
 * - Permission requests: Prompts user for notification permission
 * - Token management: Generates, stores, and refreshes FCM tokens
 * - Message handling: Sets up foreground message listeners
 *
 * @dependencies
 * - Firebase SDK: messaging, app initialization
 * - FCM token manager: token generation and storage
 * - Session storage: token persistence
 *
 * @usage
 * ```tsx
 * const {
 *   isInitialized,
 *   hasPermission,
 *   token,
 *   requestPermission,
 *   generateToken
 * } = useFirebaseNotifications({
 *   enableForegroundMessages: true,
 *   enableBackgroundMessages: true
 * });
 * ```
 * ================================================================
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { isFirebaseConfigured } from '@/lib/firebase/config';
import {
  clearFCMToken,
  generateFCMToken,
  getRetryStatus,
  getStoredFCMToken,
  refreshFCMTokenIfNeeded,
  requestNotificationPermission,
  resetPermissionRetryCount,
  setupForegroundMessageHandler,
} from '@/lib/firebase/fcmTokenManager';
import type {
  FCMTokenResult,
  FirebaseNotificationConfig,
  NotificationPermissionResult,
  UseFirebaseNotificationsResult,
} from '@/types/firebase';

interface UseFirebaseNotificationsOptions {
  /** Configuration for Firebase notifications */
  config?: Partial<FirebaseNotificationConfig>;
  /** Callback for foreground messages */
  onForegroundMessage?: (payload: any) => void;
  /** Enable automatic token refresh */
  autoRefresh?: boolean;
}

export function useFirebaseNotifications(
  options: UseFirebaseNotificationsOptions = {},
): UseFirebaseNotificationsResult {
  const { config = {}, onForegroundMessage, autoRefresh = true } = options;

  // ================================================================
  // STATE MANAGEMENT
  // ================================================================
  //
  // Local state for Firebase initialization and token management
  // Tracks initialization, permissions, tokens, and error states
  // ================================================================

  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasExceededRetryLimit, setHasExceededRetryLimit] = useState(false);

  // ================================================================
  // INITIALIZATION TRACKING
  // ================================================================
  //
  // Prevent multiple Firebase initializations using ref
  // Stable callback reference to avoid re-initialization loops
  // ================================================================

  const isInitializing = useRef(false);
  const callbackRef = useRef(onForegroundMessage);

  // Update callback ref when it changes
  useEffect(() => {
    callbackRef.current = onForegroundMessage;
  }, [onForegroundMessage]);

  // ================================================================
  // PERMISSION MANAGEMENT
  // ================================================================

  /**
   * Request notification permission from user
   *
   * @returns {Promise<NotificationPermissionResult>} Permission result
   * @async
   */
  const handleRequestPermission = useCallback(async (): Promise<NotificationPermissionResult> => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('🔔 Requesting notification permission via hook...');

      const permission = await requestNotificationPermission();
      const granted = permission === 'granted';

      setHasPermission(granted);

      if (granted) {
        console.log('✅ Notification permission granted via hook');
      } else {
        console.warn('❌ Notification permission denied via hook');
        setError('Notification permission denied');
      }

      return {
        success: granted,
        permission,
        requested: true,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Permission request failed';
      console.error('❌ Permission request error:', errorMessage);
      setError(errorMessage);

      return {
        success: false,
        permission: 'denied',
        requested: true,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ================================================================
  // TOKEN MANAGEMENT
  // ================================================================

  /**
   * Generate new FCM token
   *
   * @returns {Promise<FCMTokenResult>} Token generation result
   * @async
   */
  const handleGenerateToken = useCallback(async (): Promise<FCMTokenResult> => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('🔑 Generating FCM token via hook...');

      const result = await generateFCMToken();

      if (result.success && result.token) {
        setToken(result.token);
        setHasPermission(result.permissionGranted || false);
        console.log('✅ FCM token generated successfully via hook');
      } else {
        setError(result.error || 'Token generation failed');
        console.error('❌ FCM token generation failed:', result.error);
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Token generation failed';
      console.error('❌ Token generation error:', errorMessage);
      setError(errorMessage);

      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Refresh existing FCM token
   *
   * @returns {Promise<FCMTokenResult>} Token refresh result
   * @async
   */
  const handleRefreshToken = useCallback(async (): Promise<FCMTokenResult> => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('🔄 Refreshing FCM token via hook...');

      const result = await refreshFCMTokenIfNeeded();

      if (result.success && result.token) {
        setToken(result.token);
        console.log('✅ FCM token refreshed successfully via hook');
      } else {
        setError(result.error || 'Token refresh failed');
        console.error('❌ FCM token refresh failed:', result.error);
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Token refresh failed';
      console.error('❌ Token refresh error:', errorMessage);
      setError(errorMessage);

      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Test browser notification API directly
   *
   * @returns {Promise<boolean>} Whether test notification was shown
   */
  const testBrowserNotification = useCallback(async (): Promise<boolean> => {
    try {
      if (!('Notification' in window)) {
        console.error('❌ Browser does not support notifications');
        return false;
      }

      if (Notification.permission !== 'granted') {
        console.error('❌ Notification permission not granted');
        return false;
      }

      console.log('🧪 Testing browser notification...');
      const notification = new Notification('Test Notification', {
        body: 'This is a test to verify browser notifications work',
        icon: '/vite.svg',
        tag: 'test-notification',
      });

      notification.onclick = () => {
        console.log('🖱️ Test notification clicked');
        notification.close();
      };

      setTimeout(() => {
        notification.close();
      }, 5000);

      console.log('✅ Test notification shown successfully');
      return true;
    } catch (error) {
      console.error('❌ Test notification failed:', error);
      return false;
    }
  }, []);

  /**
   * Clear stored FCM token
   *
   * @returns {void}
   */
  const handleClearToken = useCallback(() => {
    try {
      clearFCMToken();
      setToken(null);
      console.log('🗑️ FCM token cleared via hook');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Token clear failed';
      console.error('❌ Token clear error:', errorMessage);
      setError(errorMessage);
    }
  }, []);

  /**
   * Reset FCM permission retry count
   *
   * @returns {void}
   */
  const handleResetRetryCount = useCallback(() => {
    try {
      resetPermissionRetryCount();
      setHasExceededRetryLimit(false);
      setError(null);
      console.log('🔄 FCM retry count reset via hook');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Retry count reset failed';
      console.error('❌ Retry count reset error:', errorMessage);
      setError(errorMessage);
    }
  }, []);

  // ================================================================
  // RETRY STATUS UPDATE EFFECT
  // ================================================================
  //
  // Purpose: Update retry status when relevant state changes
  // Triggers: When error state changes or after token operations
  // Updates: hasExceededRetryLimit state based on current retry status
  // ================================================================

  useEffect(() => {
    const retryStatus = getRetryStatus();
    setHasExceededRetryLimit(retryStatus.hasExceededLimit);
    
    if (retryStatus.hasExceededLimit) {
      console.warn(`🚫 FCM retry limit exceeded (${retryStatus.retryAttempts} attempts)`);
      if (retryStatus.cooldownActive && retryStatus.cooldownRemainingMinutes) {
        console.warn(`⏰ Cooldown active: ${retryStatus.cooldownRemainingMinutes} minutes remaining`);
      }
    }
  }, [error, token]); // Check retry status when error or token changes

  // ================================================================
  // INITIALIZATION EFFECT
  // ================================================================
  //
  // Initialize Firebase and set up messaging when hook mounts
  // Check for existing tokens and permissions
  // Set up foreground message handling
  // ================================================================

  useEffect(() => {
    async function initializeFirebase() {
      // Prevent multiple simultaneous initializations
      if (isInitializing.current || isInitialized) {
        console.log('🔥 Firebase already initializing or initialized, skipping...');
        return;
      }

      try {
        isInitializing.current = true;
        console.log('🔥 Initializing Firebase notifications...');

        // ================================================================
        // CONFIGURATION CHECK
        // ================================================================
        //
        // Purpose: Verify Firebase is properly configured
        // Safety: Prevent initialization with missing configuration
        // UX: Provide clear feedback when configuration is incomplete
        // ================================================================

        if (!isFirebaseConfigured()) {
          const message = 'Firebase not configured - missing environment variables';
          console.warn('⚠️', message);
          setError(message);
          setIsInitialized(false);
          return;
        }

        // ================================================================
        // EXISTING TOKEN CHECK
        // ================================================================
        //
        // Purpose: Check for existing valid token in storage
        // Performance: Avoid unnecessary token generation
        // UX: Faster initialization when token already exists
        // ================================================================

        const existingTokenInfo = getStoredFCMToken();
        if (existingTokenInfo && existingTokenInfo.isValid) {
          setToken(existingTokenInfo.token);
          setHasPermission(true);
          console.log('✅ Using existing valid FCM token:', existingTokenInfo.token);
        }

        // ================================================================
        // PERMISSION AND RETRY STATUS CHECK
        // ================================================================
        //
        // Purpose: Check current notification permission status and retry limits
        // Compatibility: Handle browsers without notification support
        // State: Update permission state without prompting user
        // ================================================================

        if ('Notification' in window) {
          const currentPermission = Notification.permission;
          setHasPermission(currentPermission === 'granted');
          console.log('🔔 Current notification permission:', currentPermission);

          // Check retry status before attempting auto-generation
          const retryStatus = getRetryStatus();
          setHasExceededRetryLimit(retryStatus.hasExceededLimit);

          if (currentPermission === 'granted' && !existingTokenInfo?.isValid) {
            // Only auto-generate token if we haven't exceeded retry limits
            if (autoRefresh && !retryStatus.hasExceededLimit) {
              console.log('🔄 Auto-generating FCM token...');
              await handleGenerateToken();
            } else if (retryStatus.hasExceededLimit) {
              console.warn('🚫 Skipping auto-generation due to retry limits');
            }
          }
        }

        // ================================================================
        // FOREGROUND MESSAGE HANDLER
        // ================================================================
        //
        // Purpose: Set up listener for foreground messages
        // Integration: Connect with existing notification system
        // Callback: Use provided callback or default handler
        // ================================================================

        if (config.enableForegroundMessages !== false && callbackRef.current) {
          const enhancedCallback = (payload: any) => {
            console.log('🔥 FOREGROUND MESSAGE RECEIVED:', payload);
            console.log('📱 Message title:', payload?.notification?.title);
            console.log('📱 Message body:', payload?.notification?.body);
            if (callbackRef.current) {
              callbackRef.current(payload);
            }
          };

          await setupForegroundMessageHandler(enhancedCallback);
          console.log('✅ Foreground message handler set up with enhanced debugging');
        }

        setIsInitialized(true);
        console.log('✅ Firebase notifications initialized successfully - SINGLE TIME');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Firebase initialization failed';
        console.error('❌ Firebase initialization error:', errorMessage);
        setError(errorMessage);
        setIsInitialized(false);
      } finally {
        isInitializing.current = false;
      }
    }

    initializeFirebase();
  }, []); // Empty dependency array to run only once

  // ================================================================
  // AUTO REFRESH EFFECT
  // ================================================================
  //
  // Purpose: Automatically refresh token when it expires
  // Interval: Check token validity periodically
  // Background: Non-blocking refresh to maintain token freshness
  // ================================================================

  useEffect(() => {
    if (!autoRefresh || !isInitialized) return;

    const checkTokenValidity = async () => {
      const tokenInfo = getStoredFCMToken();
      if (tokenInfo && !tokenInfo.isValid) {
        console.log('🔄 Auto-refreshing expired FCM token...');
        await handleRefreshToken();
      }
    };

    // Check token validity every hour
    const interval = setInterval(checkTokenValidity, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [autoRefresh, isInitialized, handleRefreshToken]);

  return {
    // State
    isInitialized,
    isLoading,
    hasPermission,
    hasExceededRetryLimit,
    token,
    error,

    // Actions
    requestPermission: handleRequestPermission,
    generateToken: handleGenerateToken,
    refreshToken: handleRefreshToken,
    clearToken: handleClearToken,
    resetRetryCount: handleResetRetryCount,
    testBrowserNotification,
  };
}

/**
 * ================================================================
 * END OF HOOK: useFirebaseNotifications
 * ================================================================
 *
 * @exports          useFirebaseNotifications - Firebase notification management hook
 * @dependencies     Firebase SDK, FCM token manager, notification types
 * @usedBy           App initialization, login flow, notification components
 *
 * @performance
 * - Initialization: Lazy initialization with configuration checks
 * - Token management: Efficient caching and refresh logic
 * - Memory: Proper cleanup of intervals and listeners
 *
 * @security
 * - Permissions: Respects user notification preferences
 * - Token storage: Secure session storage implementation
 * - Error handling: No sensitive data exposed in error messages
 *
 * ================================================================
 */
